<template>
  <div class="ak-progress-bar" :class="classes" :style="styles">
    <div
      class="ak-progress-bar__bar"
      :data-progress="newProgress"
      :style="`width: ${newProgress}%`"
    />
    <div v-if="showText" class="ak-progress-bar__text">
      {{ textValue }}
    </div>
  </div>
</template>

<script>
import { computed, ref, defineComponent, watch } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-progressbar />
   */
  name: 'AkProgressbar',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Determines if the indicator should display.
     */
    showIndicator: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Determines the position of the indicoator.
     */
    indicatorPosition: {
      validator: (i) => ['top', 'bottom'].indexOf(i) >= 0,
      type: String,
      default: 'top',
    },

    /**
     * @type {Number|String}
     * @description The value of the progress bar.
     */
    progress: {
      type: [Number, String],
      default: 0,
    },

    /**
     * @type {Boolean}
     * @description Determines if the text should display.
     */
    showText: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description The text to display in the progress bar.
     */
    text: {
      type: String,
      default: '',
    },

    /**
     * @type {String}
     * @description The color of the progress bar
     */
    color: {
      validator: (i) => ['success'].indexOf(i) >= 0,
      type: String,
      default: null,
    },

    /**
     * @type {Number|String}
     * @description The height of the progress bar.
     */
    height: {
      type: [String, Number],
      default: 20,
    },

    /**
     * @type {Boolean}
     * @description Determines if the progress bar should be inverse.
     */
    inverse: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the progress bar is animated.
     */
    animated: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description The animation to run.
     */
    animation: {
      validator: (i) =>
        ['staggered', 'for-5s', 'for-10s', 'for-15s'].indexOf(i) >= 0,
      type: String,
      default: null,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const newProgress = ref(props.progress)

    const classes = computed(() => {
      return {
        'ak-progress-bar--has-indicator': props.showIndicator,
        'ak-progress-bar--indicator-bottom':
          props.indicatorPosition === 'bottom',
        [`ak-progress-bar--${props.color}`]: props.color,
        'ak-progress-bar--inverse': props.inverse,
        'ak-progress-bar--animated': props.animated,
        [props.animation]: props.animated,
      }
    })

    const textValue = computed(() => {
      return props.text
    })

    const styles = computed(() => {
      return `height: ${props.height}px;`
    })

    watch(
      () => props.progress,
      (newVal) => {
        newProgress.value = newVal
      }
    )

    return {
      newProgress,
      textValue,
      classes,
      styles,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';
$progress-bar-background: $color-grey-lighter;

$progress-bar-height: 0.5rem;
$progress-bar-color: $color-primary;

.ak-progress-bar {
  $self: &;
  width: 100%;
  background-color: $progress-bar-background;
  border-radius: 100px;
  display: block;

  &__bar {
    width: 0;
    display: block;
    min-height: $progress-bar-height;
    text-align: center;
    color: #fff;
    padding: $spacing-tiny 0;
    background-color: $progress-bar-color;
    border-radius: 100px;
    height: 100%;
    transition: $transition;
  }

  &__text {
    position: relative;
    text-align: center;
    font-size: $font-size-small;
    font-weight: bold;
    color: #fff;
    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
    padding-bottom: 2px;
  }

  &--has-indicator {
    #{$self}__bar {
      position: relative;

      &:after,
      &:before {
        position: absolute;
        bottom: 100%;
        right: 0;
        transform: translateX(50%);
      }
      &:after {
        min-width: 2rem;
        text-align: center;
        content: attr(data-progress) '%';
        margin-bottom: 0.35rem;
        background: $progress-bar-color;
        display: block;
        border-radius: $base-border-radius;
        padding: $spacing-tiny;
        font-size: $font-size-smaller;
        font-weight: bold;
        line-height: 1;
        color: #fff;
      }
      &:before {
        content: ' ';
        margin-bottom: 0.1rem;
        @include triangle(0.5rem, $progress-bar-color, down);
      }
    }
  }

  &--indicator-bottom {
    .ak-progress-bar__bar {
      &:after,
      &:before {
        bottom: auto;
        top: 100%;
        margin-bottom: 0;
      }
      &:after {
        margin-top: 0.25rem;
      }
      &:before {
        border: 0;
        @include triangle(0.5rem, $progress-bar-color, up);
      }
    }
  }

  &--has-indicator {
    position: relative;
    .ak-progress-bar__bar {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
    }
  }

  &--inverse {
    background-color: #fff;
    .ak-progress-bar__bar {
      &:after {
        background-color: #fff;
        color: $color-text;
      }
      &:before {
        border-top-color: #fff;
      }
    }
  }
}

.ak-progress-bar--success .ak-progress-bar__bar,
.ak-progress-bar__bar--success {
  background-color: $color-success;
  &:after {
    background-color: $color-success;
  }
  &:before {
    border-top-color: $color-success;
    border-bottom-color: $color-success;
  }
}

@keyframes progressbar {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

@keyframes progressbarStaggered {
  0% {
    width: 0;
  }
  20% {
    width: 30%;
  }
  30% {
    width: 40%;
  }
  60% {
    width: 75%;
  }
  100% {
    width: 100%;
  }
}
.ak-progress-bar--animated {
  .ak-progress-bar__bar {
    animation-name: progressbar;
    animation-duration: 2s;
    animation-timing-function: ease;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &.for-5s {
    .ak-progress-bar__bar {
      animation-duration: 5s;
    }
  }
  &.for-10s {
    .ak-progress-bar__bar {
      animation-duration: 10s;
    }
  }
  &.for-15s {
    .ak-progress-bar__bar {
      animation-duration: 15s;
    }
  }
  &.staggered {
    .ak-progress-bar__bar {
      animation-name: progressbarStaggered;
    }
  }
}
</style>
